import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../util/Icon";

export function DropdownPanel({
  panelRef,
  options,
  initialPending = [],
  getOptionLabel,
  getOptionValue,
  onConfirm,
  onCancel,
  style,
}) {
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(initialPending);

  const pendingValues = pending.map(getOptionValue);
  const visible = options.filter((o) =>
    getOptionLabel(o).toLowerCase().includes(search.toLowerCase())
  );
  const allChecked = pending.length === options.length;
  const someChecked = pending.length > 0 && pending.length < options.length;

  const toggleAll = () => setPending(allChecked ? [] : [...options]);
  const toggle = (opt) => {
    const v = getOptionValue(opt);
    setPending((prev) =>
      prev.some((p) => getOptionValue(p) === v)
        ? prev.filter((p) => getOptionValue(p) !== v)
        : [...prev, opt]
    );
  };

  return (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        zIndex: 9999,
        background: "#fff",
        border: "1px solid #dee2e6",
        borderRadius: 4,
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        minWidth: 220,
        padding: 10,
        fontSize: "0.85rem",
        ...style,
      }}
    >
      <input
        className="form-control form-control-sm mb-2"
        placeholder="Search…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <div
        style={{
          maxHeight: 200,
          overflowY: "auto",
          borderTop: "1px solid #dee2e6",
          paddingTop: 6,
        }}
      >
        <label
          className="d-flex align-items-center gap-2 py-1 px-1"
          style={{ cursor: "pointer", fontWeight: 600 }}
        >
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => { if (el) el.indeterminate = someChecked; }}
            onChange={toggleAll}
          />
          (Select All)
        </label>
        {visible.map((opt) => {
          const v = getOptionValue(opt);
          return (
            <label
              key={String(v)}
              className="d-flex align-items-center gap-2 py-1 px-1"
              style={{ cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={pendingValues.includes(v)}
                onChange={() => toggle(opt)}
              />
              {getOptionLabel(opt) || (
                <span className="text-muted fst-italic">(blank)</span>
              )}
            </label>
          );
        })}
      </div>
      <div
        className="d-flex gap-2 mt-2 pt-2"
        style={{ borderTop: "1px solid #dee2e6" }}
      >
        <button
          className="btn btn-primary btn-sm flex-fill"
          onClick={() => onConfirm(pending)}
        >
          OK
        </button>
        <button
          className="btn btn-outline-secondary btn-sm flex-fill"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function DropdownMultiSelect({
  options = [],
  value = [],
  onChange,
  getOptionLabel = (o) => o.label ?? String(o),
  getOptionValue = (o) => o.value ?? o,
  placeholder = "Select...",
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 220 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const handleOpen = () => {
    if (disabled) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 220);
    const PANEL_HEIGHT = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const pos = spaceBelow >= PANEL_HEIGHT || spaceBelow >= rect.top
      ? { top: rect.bottom + 2, left: rect.left, width }
      : { bottom: window.innerHeight - rect.top + 2, left: rect.left, width };
    setPanelPos(pos);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        !panelRef.current?.contains(e.target) &&
        !triggerRef.current?.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className={`form-control d-flex align-items-center${className ? " " + className : ""}`}
        title={value.length > 0 ? value.map(getOptionLabel).join(", ") : undefined}
        style={{
          cursor: disabled ? "default" : "pointer",
          height: 38,
          userSelect: "none",
          opacity: disabled ? 0.65 : 1,
          paddingRight: 28,
          position: "relative",
        }}
      >
        {value.length === 0 ? (
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            {placeholder}
          </span>
        ) : (
          <span style={{ fontSize: "0.875rem" }}>
            {value.length} selected
          </span>
        )}
        <Icon
          iconName="ChevronDown"
          size={12}
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#adb5bd",
            pointerEvents: "none",
          }}
        />
      </div>

      {open &&
        createPortal(
          <DropdownPanel
            panelRef={panelRef}
            options={options}
            initialPending={[...value]}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            onConfirm={(selected) => {
              onChange(selected);
              setOpen(false);
            }}
            onCancel={() => setOpen(false)}
            style={panelPos}
          />,
          document.body
        )}
    </>
  );
}
